import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import '../i18next';
import { fetchPinned, fetchNoti } from '../utils/notifications';

const NotificationContext = createContext();

const SEAT_TAKEOVER_MS = 9000; // how long a seat update overrides the pinned item

// Polling intervals (ms) are tunable at runtime via public/config.js. Read
// lazily (config.js is loaded by an async script tag), with safe fallbacks.
const NOTI_POLL_INTERVAL = 25000;
const PINNED_POLL_INTERVAL = 35000;

export const NotificationContextProvider = ({ children }) => {
  const [notiPinned, setNotiPinned] = useState([]); // admin notifications
  const [storyPins, setStoryPins] = useState([]); // pinned news stories
  const [activeIndex, setActiveIndex] = useState(0);
  const [seatUpdate, setSeatUpdate] = useState(null);
  const [liveVideo, setLiveVideo] = useState(null);

  // Admin notification(s) first, then the pinned news stories.
  const pinnedItems = useMemo(
    () => [...notiPinned, ...storyPins],
    [notiPinned, storyPins],
  );

  const lastSeenSeatTimeRef = useRef(-1); // -1 => first load not yet recorded
  const takeoverTimerRef = useRef(null);

  const showSeatTakeover = useCallback((item) => {
    setSeatUpdate(item);
    if (takeoverTimerRef.current) clearTimeout(takeoverTimerRef.current);
    takeoverTimerRef.current = setTimeout(() => {
      setSeatUpdate(null);
      takeoverTimerRef.current = null;
    }, SEAT_TAKEOVER_MS);
  }, []);

  // Keep the carousel index within bounds as the combined list changes.
  useEffect(() => {
    setActiveIndex((i) =>
      pinnedItems.length ? Math.min(i, pinnedItems.length - 1) : 0,
    );
  }, [pinnedItems.length]);

  // Poll admin notifications, seat updates, and live video.
  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const noti = await fetchNoti();
      if (cancelled) return;

      setNotiPinned(noti.notiPinned);
      setLiveVideo(noti.liveVideo ?? null);

      const updates = noti.updates;
      if (updates.length) {
        const newest = updates.reduce(
          (m, u) => (u.time > m.time ? u : m),
          updates[0],
        );
        if (lastSeenSeatTimeRef.current < 0) {
          // First load: record as seen, no takeover.
          lastSeenSeatTimeRef.current = newest.time;
        } else if (newest.time > lastSeenSeatTimeRef.current) {
          lastSeenSeatTimeRef.current = newest.time;
          showSeatTakeover(newest);
        }
      }
    };

    poll();
    const interval = setInterval(
      poll,
      window.notiPollInterval || NOTI_POLL_INTERVAL,
    );
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (takeoverTimerRef.current) clearTimeout(takeoverTimerRef.current);
    };
  }, [showSeatTakeover]);

  // Poll pinned news stories on a separate, slower cadence.
  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const pins = await fetchPinned();
      if (cancelled) return;
      setStoryPins(pins);
    };

    poll();
    const interval = setInterval(
      poll,
      window.pinnedPollInterval || PINNED_POLL_INTERVAL,
    );
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const next = useCallback(
    () =>
      setActiveIndex((i) =>
        pinnedItems.length ? (i + 1) % pinnedItems.length : 0,
      ),
    [pinnedItems.length],
  );

  const prev = useCallback(
    () =>
      setActiveIndex((i) =>
        pinnedItems.length
          ? (i - 1 + pinnedItems.length) % pinnedItems.length
          : 0,
      ),
    [pinnedItems.length],
  );

  return (
    <NotificationContext.Provider
      value={{ pinnedItems, activeIndex, next, prev, seatUpdate, liveVideo }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
